<template>
    <v-dialog :model-value="visible" persistent max-width="860" @update:model-value="$emit('update:visible', $event)">
        <v-card
            ref="scheduleCard"
            class="d-flex flex-column overflow-hidden"
            :style="scheduleCardStyle"
        >
            <v-card-title class="text-wrap flex-shrink-0">{{ $t("reservations.scheduleEditTitle") }}</v-card-title>
            <v-card-text class="schedule-dialog-body pt-2 overflow-y-auto overflow-x-hidden">
                <v-form ref="formRef">
                    <DividerTitle :title="$t('reservation.loadingSection')" />
                    <div
                        v-for="(row, index) in loadingSchedules"
                        :key="`loading-${row.locationId}`"
                        class="reserve-stop-row mb-2"
                    >
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    :model-value="row.placeLabel"
                                    :label="$t('reservation.loadingSection')"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    readonly
                                    disabled
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-tooltip
                                    :text="row.constraints.dateDisabledTooltip"
                                    location="top"
                                    :disabled="!row.constraints.dateDisabled"
                                >
                                    <template #activator="{ props }">
                                        <div v-bind="props">
                                            <FormDate
                                                ref="loadingDateFields"
                                                :model-value="row.schedule"
                                                :data-cy-prefix="`card-schedule-loading-date-${index}`"
                                                single
                                                density="compact"
                                                :min="row.constraints.dateMin"
                                                :max="row.constraints.dateMax"
                                                :disabled="row.constraints.dateDisabled"
                                                @update:model-value="row.schedule = $event"
                                            />
                                        </div>
                                    </template>
                                </v-tooltip>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-tooltip
                                    :text="row.constraints.timeDisabledTooltip"
                                    location="top"
                                    :disabled="!row.constraints.timeDisabled"
                                >
                                    <template #activator="{ props }">
                                        <div v-bind="props">
                                            <FormTime
                                                :model-value="row.schedule"
                                                :data-cy-prefix="`card-schedule-loading-time-${index}`"
                                                required
                                                density="compact"
                                                :min="row.constraints.timeMin"
                                                :max="row.constraints.timeMax"
                                                :disabled="row.constraints.timeDisabled"
                                                @update:model-value="row.schedule = $event"
                                            />
                                        </div>
                                    </template>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </div>

                    <DividerTitle :title="$t('reservation.unloadingSection')" />
                    <div
                        v-for="(row, index) in unloadingSchedules"
                        :key="`unloading-${row.locationId}`"
                        class="reserve-stop-row mb-2"
                    >
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    :model-value="row.placeLabel"
                                    :label="$t('reservation.unloadingSection')"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    readonly
                                    disabled
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-tooltip
                                    :text="row.constraints.dateDisabledTooltip"
                                    location="top"
                                    :disabled="!row.constraints.dateDisabled"
                                >
                                    <template #activator="{ props }">
                                        <div v-bind="props">
                                            <FormDate
                                                ref="unloadingDateFields"
                                                :model-value="row.schedule"
                                                :data-cy-prefix="`card-schedule-unloading-date-${index}`"
                                                single
                                                density="compact"
                                                :min="row.constraints.dateMin"
                                                :max="row.constraints.dateMax"
                                                :disabled="row.constraints.dateDisabled"
                                                @update:model-value="row.schedule = $event"
                                            />
                                        </div>
                                    </template>
                                </v-tooltip>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-tooltip
                                    :text="row.constraints.timeDisabledTooltip"
                                    location="top"
                                    :disabled="!row.constraints.timeDisabled"
                                >
                                    <template #activator="{ props }">
                                        <div v-bind="props">
                                            <FormTime
                                                :model-value="row.schedule"
                                                :data-cy-prefix="`card-schedule-unloading-time-${index}`"
                                                required
                                                density="compact"
                                                :min="row.constraints.timeMin"
                                                :max="row.constraints.timeMax"
                                                :disabled="row.constraints.timeDisabled"
                                                @update:model-value="row.schedule = $event"
                                            />
                                        </div>
                                    </template>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </div>

                    <v-textarea
                        v-model="changeReason"
                        class="mt-4"
                        variant="outlined"
                        density="compact"
                        rows="3"
                        hide-details="auto"
                        :rules="changeReasonRules"
                        :label="$t('reservations.scheduleChangeReason')"
                    />
                </v-form>
            </v-card-text>
            <v-card-actions class="px-4 pb-4 pt-0 flex-shrink-0">
                <v-row dense class="w-100 ma-0">
                    <v-col cols="6" class="pa-1">
                        <v-btn block variant="flat" color="error" @click="$emit('update:visible', false)">
                            {{ $t("reservation.cancel") }}
                        </v-btn>
                    </v-col>
                    <v-col cols="6" class="pa-1">
                        <v-btn block variant="flat" color="primary" :loading="submitting" @click="submitSchedulePatch">
                            {{ $t("common.save") }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import DividerTitle from "@/components/forms/DividerTitle.vue";
import FormDate from "@/components/forms/FormDate.vue";
import FormTime from "@/components/forms/FormTime.vue";
import { formatToHhMm, formatToYmd } from "@/utils";

export default {
    name: "ReservationScheduleEditModal",
    components: { DividerTitle, FormDate, FormTime },
    props: {
        visible: { type: Boolean, default: false },
        reservation: { type: Object, required: true },
        entryType: { type: String, required: true },
        isEntryOwner: { type: Boolean, required: true },
    },
    emits: ["update:visible", "saved", "error"],
    data() {
        return {
            submitting: false,
            changeReason: "",
            loadingSchedules: [],
            unloadingSchedules: [],
            scheduleCardMaxHeight: null,
            changeReasonRules: [(value) => !!(value && String(value).trim()) || this.$t("validation.required")],
        };
    },
    computed: {
        wareTransporterEditingOwnReservation() {
            return this.entryType === "ware" && !this.isEntryOwner;
        },
        scheduleCardStyle() {
            if (!this.scheduleCardMaxHeight) return {};
            return { maxHeight: this.scheduleCardMaxHeight };
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(nextVisible) {
                if (nextVisible) {
                    this.changeReason = "";
                    this.scheduleCardMaxHeight = null;
                    this.prefillFromReservation();
                    this.$nextTick(() => {
                        requestAnimationFrame(() => this.applyScheduleCardCap());
                    });
                } else {
                    this.scheduleCardMaxHeight = null;
                }
            },
        },
        "reservation.uid"() {
            if (this.visible) this.prefillFromReservation();
        },
    },
    methods: {
        prefillFromReservation() {
            const reservationData = this.reservation || {};
            const summary = reservationData.entry_summary || {};
            const loadingStops = Array.isArray(summary.loading_stops) ? summary.loading_stops : [];
            const unloadingStops = Array.isArray(summary.unloading_stops) ? summary.unloading_stops : [];
            this.loadingSchedules = loadingStops.length
                ? loadingStops.map((stop) => this.rowFromStop(stop))
                : [
                      this.rowFromLegacy(
                          "loading",
                          summary.loading_summary,
                          summary.entry_loading_window,
                          reservationData.chosen_loading_date,
                          reservationData.chosen_loading_time_start,
                          reservationData.chosen_loading_time_end
                      ),
                  ];
            this.unloadingSchedules = unloadingStops.length
                ? unloadingStops.map((stop) => this.rowFromStop(stop))
                : [
                      this.rowFromLegacy(
                          "unloading",
                          summary.unloading_summary,
                          summary.entry_unloading_window,
                          reservationData.chosen_unloading_date,
                          reservationData.chosen_unloading_time_start,
                          reservationData.chosen_unloading_time_end
                      ),
                  ];
        },
        rowFromStop(stop) {
            return {
                locationId: stop.location_id,
                role: stop.role,
                placeLabel: [stop.country_name, stop.city].filter(Boolean).join(", "),
                constraints: this.constraintsFromWindow(stop.entry_window || {}),
                schedule: this.dateAndTimesFromIso(stop.scheduled_date_iso, stop.time_start, stop.time_end),
            };
        },
        rowFromLegacy(role, summaryText, entryWindow, dateValue, timeStart, timeEnd) {
            return {
                locationId: role,
                role,
                placeLabel: summaryText || "",
                constraints: this.constraintsFromWindow(entryWindow || {}),
                schedule: this.dateAndTimesFromIso(this.isoPrefixFromValue(dateValue), timeStart, timeEnd),
            };
        },
        constraintsFromWindow(entryWindow) {
            const result = {
                dateMin: null,
                dateMax: null,
                dateDisabled: false,
                dateDisabledTooltip: "",
                timeMin: null,
                timeMax: null,
                timeDisabled: false,
                timeDisabledTooltip: "",
            };
            if (!this.wareTransporterEditingOwnReservation) return result;
            const dateStart = entryWindow.date_start_iso || null;
            const dateEnd = entryWindow.date_end_iso || null;
            if (dateStart && dateEnd) {
                result.dateMin = dateStart;
                result.dateMax = dateEnd;
                if (dateStart === dateEnd) {
                    result.dateDisabled = true;
                    result.dateDisabledTooltip = this.$t("reservation.singleDateTooltip");
                }
            }
            const startText = entryWindow.time_start || "";
            const endText = entryWindow.time_end || "";
            if (startText && endText && startText === endText) {
                result.timeMin = startText;
                result.timeMax = endText;
                result.timeDisabled = true;
                result.timeDisabledTooltip = this.$t("reservation.singleTimeTooltip");
            } else if (startText || endText) {
                result.timeMin = startText || "00:00";
                result.timeMax = endText || "23:59";
            }
            return result;
        },
        applyScheduleCardCap(attempt = 0, previousFormHeight = 0) {
            if (!this.visible) return;
            const cardComponent = this.$refs.scheduleCard;
            const cardEl = cardComponent?.$el || cardComponent;
            if (!cardEl) return;
            const extraRows =
                Math.max(0, this.loadingSchedules.length - 1) +
                Math.max(0, this.unloadingSchedules.length - 1);
            if (extraRows <= 0) {
                this.scheduleCardMaxHeight = null;
                return;
            }
            const form = cardEl.querySelector("form");
            const title = cardEl.querySelector(".v-card-title");
            const body = cardEl.querySelector(".schedule-dialog-body");
            const actions = cardEl.querySelector(".v-card-actions");
            const sampleRow = cardEl.querySelector(".reserve-stop-row");
            const formHeight = form?.scrollHeight || 0;
            const laidOut =
                sampleRow &&
                title &&
                body &&
                actions &&
                formHeight > 200 &&
                formHeight === previousFormHeight;
            if (!laidOut) {
                if (attempt < 40) {
                    setTimeout(() => this.applyScheduleCardCap(attempt + 1, formHeight), 50);
                }
                return;
            }
            const rowStyle = window.getComputedStyle(sampleRow);
            const rowHeight =
                sampleRow.offsetHeight +
                (parseFloat(rowStyle.marginTop) || 0) +
                (parseFloat(rowStyle.marginBottom) || 0);
            const titleStyle = window.getComputedStyle(title);
            const bodyStyle = window.getComputedStyle(body);
            const chrome =
                title.offsetHeight +
                (parseFloat(titleStyle.marginTop) || 0) +
                (parseFloat(titleStyle.marginBottom) || 0) +
                (parseFloat(bodyStyle.paddingTop) || 0) +
                (parseFloat(bodyStyle.paddingBottom) || 0) +
                actions.offsetHeight;
            const naturalHeight = chrome + formHeight - extraRows * rowHeight;
            if (naturalHeight > 200) {
                this.scheduleCardMaxHeight = `${Math.ceil(naturalHeight)}px`;
            }
        },
        isoPrefixFromValue(value) {
            if (value == null || value === "") return "";
            const text = String(value).trim();
            return text.length >= 10 ? text.slice(0, 10) : "";
        },
        async validateDateFields(refName) {
            const refs = this.$refs[refName];
            const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
            if (!list.length) return false;
            let isValid = true;
            for (const component of list) {
                const valid = await component?.validateFields?.();
                if (!valid) isValid = false;
            }
            return isValid;
        },
        dateAndTimesFromIso(isoPrefix, timeStartRaw, timeEndRaw) {
            const base = {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: formatToHhMm(timeStartRaw) || "",
                timeEnd: formatToHhMm(timeEndRaw) || "",
            };
            if (!isoPrefix || isoPrefix.length < 10) return base;
            const dateObject = new Date(`${isoPrefix}T12:00:00`);
            if (Number.isNaN(dateObject.getTime())) return base;
            return {
                date: [dateObject],
                dateStart: isoPrefix,
                dateEnd: isoPrefix,
                timeStart: base.timeStart,
                timeEnd: base.timeEnd,
            };
        },
        async submitSchedulePatch() {
            const formValidation = await this.$refs.formRef?.validate();
            if (!formValidation?.valid) return;

            const loadingDateOk = await this.validateDateFields("loadingDateFields");
            const unloadingDateOk = await this.validateDateFields("unloadingDateFields");
            if (!loadingDateOk || !unloadingDateOk) return;

            const scheduleItem = (row) => {
                const dateIso = formatToYmd(row.schedule.date?.[0]) || row.schedule.dateStart || "";
                return {
                    location_id: row.locationId,
                    role: row.role,
                    date: dateIso,
                    time_start: row.schedule.timeStart,
                    time_end: row.schedule.timeEnd,
                };
            };
            const loadingItems = this.loadingSchedules.map(scheduleItem);
            const unloadingItems = this.unloadingSchedules.map(scheduleItem);
            const primaryLoading = loadingItems[0];
            const primaryUnloading = unloadingItems[unloadingItems.length - 1];
            if (
                !primaryLoading?.date ||
                !primaryUnloading?.date ||
                loadingItems.some((item) => !item.date || !item.time_start || !item.time_end) ||
                unloadingItems.some((item) => !item.date || !item.time_start || !item.time_end)
            ) {
                return;
            }

            const trimmedReason = String(this.changeReason || "").trim();
            if (!trimmedReason) return;

            this.submitting = true;
            try {
                await axios.patch(
                    `${process.env.VUE_APP_API_URL}/reservations/${this.reservation.uid}/schedule/`,
                    {
                        stop_schedules: [...loadingItems, ...unloadingItems],
                        chosen_loading_date: primaryLoading.date,
                        chosen_loading_time_start: primaryLoading.time_start,
                        chosen_loading_time_end: primaryLoading.time_end,
                        chosen_unloading_date: primaryUnloading.date,
                        chosen_unloading_time_start: primaryUnloading.time_start,
                        chosen_unloading_time_end: primaryUnloading.time_end,
                        change_reason: trimmedReason,
                    }
                );
                this.$emit("update:visible", false);
                this.$emit("saved");
            } catch (error) {
                const errorCode = error.response?.data?.error;
                this.$emit("error", errorCode || "schedule_patch_failed");
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style scoped>
.schedule-dialog-body {
    min-height: 0;
}
</style>
